import { useEffect, useRef, useState } from 'react';
import { getDateDaysAgo } from '@/shared/date';

type UseTimeMachineParams = {
  coinId?: string;
  days?: number;
};

type TimeMachineResult = {
  price?: number;
  isLoading: boolean;
  error?: string;
};

const MAX_DAYS = 365;
const DEBOUNCE_MS = 500;

export const useTimeMachine = ({
  coinId,
  days,
}: UseTimeMachineParams): TimeMachineResult => {
  const [price, setPrice] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!coinId || days === undefined || Number.isNaN(days)) {
      setPrice(undefined);
      setError(undefined);
      setIsLoading(false);
      return;
    }

    if (days > MAX_DAYS) {
      setPrice(undefined);
      setError(`Max range is ${MAX_DAYS} days`);
      setIsLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const date = getDateDaysAgo(days);
      const url = `https://api.coingecko.com/api/v3/coins/${encodeURIComponent(
        coinId,
      )}/history?date=${date}`;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      setError(undefined);

      fetch(url, { signal: controller.signal })
        .then((res) => {
          if (!res.ok) {
            throw new Error('Failed to fetch price history');
          }
          return res.json();
        })
        .then((data) => {
          const fetchedPrice =
            data?.market_data?.current_price?.usdt ??
            data?.market_data?.current_price?.usd;
          if (typeof fetchedPrice !== 'number') {
            throw new Error('Price not available');
          }
          setPrice(fetchedPrice);
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          setPrice(undefined);
          setError(err.message || 'Unable to load time machine data');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      abortRef.current?.abort();
    };
  }, [coinId, days]);

  return { price, isLoading, error };
};
